import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Truck, TruckSearchFilters } from "../../types/truck";
import { searchTrucksApi } from "../../api/trucksApi";
import type { RootState } from "../index";
import type { PaginationResponse, PaginationParam } from "../../types/paginationResponse";

interface TruckState {
  trucks: Truck[];
  loading: boolean;
  error: string | null;
  pagination: PaginationResponse | null;
}

const initialState: TruckState = {
  trucks: [],
  loading: false,
  error: null,
  pagination: null,
};

export const searchTrucks = createAsyncThunk<
  { trucks: Truck[]; pagination: PaginationResponse },
  { filters?: TruckSearchFilters; pagination?: PaginationParam },
  { rejectValue: string }
>("trucks/searchTrucks", async ({ filters, pagination }, { rejectWithValue }) => {
  try {
    return await searchTrucksApi(filters, pagination);
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || "Failed to search trucks");
  }
});

const truckSlice = createSlice({
  name: "trucks",
  initialState,
  reducers: {
    clearTrucksError: (state) => {
      state.error = null;
    },
    updateTruck: (state, action: PayloadAction<Truck>) => {
      const index = state.trucks.findIndex((truck) => truck._id === action.payload._id);
      if (index !== -1) {
        state.trucks[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchTrucks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchTrucks.fulfilled, (state, action) => {
        state.loading = false;
        state.trucks = action.payload.trucks;
        state.pagination = action.payload.pagination;
      })
      .addCase(searchTrucks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to search trucks";
      });
  },
});

export const { clearTrucksError, updateTruck } = truckSlice.actions;

// Selectors
export const selectTrucks = (state: RootState) => state.trucks.trucks;
export const selectTrucksLoading = (state: RootState) => state.trucks.loading;
export const selectTrucksError = (state: RootState) => state.trucks.error;
export const selectTrucksPagination = (state: RootState) => state.trucks.pagination;

export default truckSlice.reducer;
